import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import type { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'
import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import type { ArcEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { EntityObjectUtils } from '@/plugins/cad/module/CADModule/object/entityObject/entityObjectUtils'
import { createCadLine2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { BaseObjectManager, HighlightMode } from './BaseObjectManager'

/**
 * 圆弧实体的渲染对象管理器，共享几何体/材质缓存并处理高亮切换。
 */
export class ArcObjectManager extends BaseObjectManager<ArcEntity, Line2> {
  // 缓存的几何体
  private cachedGeometryMap: Map<string, LineGeometry> = new Map()
  // 已使用缓存几何体的构件
  private usedCacheGeometryEntityMap: Map<string, Set<string>> = new Map()
  // entity的原材质
  private entityOriginalMaterialMap: Map<string, LineMaterial> = new Map()

  protected createObject(entity: ArcEntity) {
    const geometry = this.getCachedGeometry(entity)
    const material = this.getCachedMaterial(entity)
    const object = createCadLine2Object(geometry, material)
    object.position.set(entity.center.x, entity.center.y, 0)
    object.computeLineDistances()
    this.entityOriginalMaterialMap.set(entity.id, object.material)
    return object
  }

  protected onUpdateGeometry(entity: ArcEntity): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    this.disposeGeometryFromObject(object)
    object.geometry = this.getCachedGeometry(entity)
    object.position.set(entity.center.x, entity.center.y, 0)
    object.computeLineDistances()
  }

  protected onUpdateMaterial(entity: ArcEntity): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    this.disposeMaterialFromObject(object)
    object.material = this.getCachedMaterial(entity)
    object.material.needsUpdate = true

    this.entityOriginalMaterialMap.set(entity.id, object.material)
    this.updateEntityHighlight(entity)
  }

  protected disposeObject(object: Line2): void {
    this.disposeGeometryFromObject(object)
    this.disposeMaterialFromObject(object)
    this.entityOriginalMaterialMap.delete(object.userData.entity.id)
  }

  protected handleEntityHighlight(entity: ArcEntity, mode: HighlightMode): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    if (mode === HighlightMode.None) {
      // 取消高亮
      object.material = this.entityOriginalMaterialMap.get(entity.id)!
      object.material.needsUpdate = true
      object.computeLineDistances()
    } else {
      // 高亮
      const material =
        mode === HighlightMode.Hover
          ? this.materialManager.getCachedHoverMaterial(entity)
          : this.materialManager.getCachedSelectMaterial(entity)
      object.material = material
      object.material.needsUpdate = true
      object.computeLineDistances()
    }
  }

  private getCachedGeometryKey(entity: ArcEntity) {
    const segmentsNum = this.resolveSegmentsNum(entity)
    return `${entity.radius}_${entity.startAngle}_${entity.endAngle}_${entity.clockwise}_${segmentsNum}`
  }

  private getCachedGeometry(entity: ArcEntity) {
    const key = this.getCachedGeometryKey(entity)
    if (!this.cachedGeometryMap.has(key)) {
      const geometry = this.createGeometry(entity)
      geometry.userData = { cacheKey: key }
      this.cachedGeometryMap.set(key, geometry)
      geometry.computeBoundingBox()
      geometry.computeBoundingSphere()
    }
    if (!this.usedCacheGeometryEntityMap.has(key)) {
      this.usedCacheGeometryEntityMap.set(key, new Set())
    }
    this.usedCacheGeometryEntityMap.get(key)!.add(entity.id)
    return this.cachedGeometryMap.get(key)!
  }

  private disposeGeometryFromObject(object: Line2) {
    const geometry = object.geometry
    const cacheKey = geometry.userData.cacheKey
    const entity = object.userData.entity as ArcEntity
    const usedSet = this.usedCacheGeometryEntityMap.get(cacheKey)
    usedSet?.delete(entity.id)
    if (usedSet && usedSet.size === 0) {
      this.cachedGeometryMap.delete(cacheKey)
      this.usedCacheGeometryEntityMap.delete(cacheKey)
      geometry.dispose()
    }
  }

  // 创建圆弧几何体使用原点作为中心点，后续通过对象变换进行位置调整
  private createGeometry(entity: ArcEntity) {
    const points = EntityObjectUtils.getArcSamplePoints(entity, true)
    return DrawUtils.getLineGeometryByPoints(points)
  }

  /** 基于弓高误差计算圆弧段数 */
  private resolveSegmentsNum(entity: ArcEntity): number {
    const segmentsNum = EntityObjectUtils.getArcSegments(entity)
    entity.segmentsNum = segmentsNum
    return segmentsNum
  }
}
